/**
 * Sentry error monitoring for L∞seEnd™ — errors and stack traces only.
 *
 * Disabled by design: Session Replay, logs, tracing, metrics, profiling,
 * user feedback, screenshots, AI monitoring, user identification.
 */

import * as Sentry from "@sentry/tanstackstart-react";

import { APP_VERSION } from "@/lib/analytics/events";
import {
  IGNORE_ERRORS,
  isCurrentRouteSensitive,
  isExpectedNonError,
  sanitizeSentryBreadcrumb,
  sanitizeSentryEvent,
  type BreadcrumbLike,
  type SentryEventLike,
} from "./privacy";

/** Explicit, auditable data-collection posture (asserted by tests). */
export const SENTRY_DATA_COLLECTION = {
  userInfo: false,
  httpBodies: [] as string[],
} as const;

let initialized = false;

function safe<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

export function sentryDsn(): string {
  return (import.meta.env["VITE_SENTRY_DSN"] as string | undefined)?.trim() ?? "";
}

export function sentryEnvironment(): "development" | "staging" | "production" {
  if (import.meta.env.MODE === "development") return "development";
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") return "development";
    if (host.includes("id-preview") || host.endsWith("-dev.lovable.app")) return "staging";
    return "production";
  }
  return "production";
}

export function isDevelopmentDiagnosticsEnabled(): boolean {
  return sentryEnvironment() !== "production";
}

export function buildSentryOptions(dsn: string = sentryDsn()) {
  return {
    dsn,
    environment: sentryEnvironment(),
    release: `looseend@${APP_VERSION}`,
    // Privacy posture
    sendDefaultPii: false,
    dataCollection: SENTRY_DATA_COLLECTION,
    // Everything except errors + stack traces stays off.
    enableLogs: false,
    tracesSampleRate: 0,
    profilesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    attachStacktrace: true,
    autoSessionTracking: false,
    sendClientReports: false,
    integrations: [] as unknown[],
    ignoreErrors: IGNORE_ERRORS,
    beforeSend: (event: SentryEventLike) => sanitizeSentryEvent(event),
    beforeBreadcrumb: (breadcrumb: BreadcrumbLike) => sanitizeSentryBreadcrumb(breadcrumb),
  };
}

/** Initialization must never break the app; a missing DSN disables Sentry. */
export function initSentry(dsn: string = sentryDsn()): boolean {
  if (initialized) return true;
  const options = buildSentryOptions(dsn);
  if (!options.dsn) return false;
  const ok = safe(() => {
    Sentry.init(options as unknown as Parameters<typeof Sentry.init>[0]);
    return true;
  });
  initialized = ok === true;
  return initialized;
}

export function isSentryInitialized(): boolean {
  return initialized;
}

export type SafeErrorTags = {
  application_surface?: string;
  error_category?: string;
};

/** Capture with controlled tags only — never props, state or user content. */
export function captureSafeException(error: unknown, tags: SafeErrorTags = {}): void {
  if (!initialized) return;
  if (isCurrentRouteSensitive()) return;
  if (isExpectedNonError(error)) return;
  safe(() =>
    Sentry.captureException(error, {
      tags: {
        ...tags,
        environment: sentryEnvironment(),
        app_version: APP_VERSION,
      },
    }),
  );
}

/** Development-only verification helper. */
export function sendSentryTestError(): boolean {
  if (!isDevelopmentDiagnosticsEnabled()) return false;
  const error = new Error("Sentry Test Error — Development Only");
  if (!initialized) return false;
  safe(() =>
    Sentry.captureException(error, {
      tags: { application_surface: "settings", error_category: "diagnostics_test" },
    }),
  );
  return true;
}
